import { appTenantState } from '@stores/bootstrap-stores/tenant';
import { appUserStore } from '@stores/bootstrap-stores/user';
import {
  Module,
  Permission,
  permissions,
  ResourceByOp,
  ResourceType
} from '@vincerosoft/assessfluence-types';

const permOperations = {
  own: ['read-own', 'write-own', 'modify-own'],
  group: ['read-group', 'write-group', 'modify-group'],
  all: ['read-all', 'write-all', 'modify-all'],
  global: ['read', 'write', 'modify']
};

// Discriminated union type mapping level + operation to valid resources
type OperationResourceMap = {
  own: {
    read: ResourceByOp['read-own'][number];
    write: ResourceByOp['write-own'][number];
    modify: ResourceByOp['modify-own'][number];
  };
  group: {
    read: ResourceByOp['read-group'][number];
    write: ResourceByOp['write-group'][number];
    modify: ResourceByOp['modify-group'][number];
  };
  all: {
    read: ResourceByOp['read-all'][number];
    write: ResourceByOp['write-all'][number];
    modify: ResourceByOp['modify-all'][number];
  };
  global: {
    read: ResourceByOp['read'][number];
    write: ResourceByOp['write'][number];
    modify: ResourceByOp['modify'][number];
  };
};

const SLICE_LAST_TWO = -2;
const SLICE_LAST = -1;

const getOperationsList = (
  operationType: keyof typeof permOperations,
  op: 'read' | 'write' | 'modify'
) => {
  const operationsSlice = {
    read: () => permOperations[operationType],
    write: () => permOperations[operationType].slice(SLICE_LAST_TWO),
    modify: () => permOperations[operationType].slice(SLICE_LAST)
  };
  return operationsSlice[op]();
};

const permissionsCheck = (type: keyof typeof permOperations, op: 'read' | 'write' | 'modify') => {
  const operationsStrategy = {
    own: {
      read: () => permOperations.own.concat(permOperations.group, permOperations.all),
      write: () =>
        getOperationsList('own', 'write').concat(
          getOperationsList('group', 'write'),
          getOperationsList('all', 'write')
        ),
      modify: () =>
        getOperationsList('own', 'modify').concat(
          getOperationsList('group', 'modify'),
          getOperationsList('all', 'modify')
        )
    },
    group: {
      read: () => permOperations.group.concat(permOperations.all),
      write: () => getOperationsList('group', 'write').concat(getOperationsList('all', 'write')),
      modify: () => getOperationsList('group', 'modify').concat(getOperationsList('all', 'modify'))
    },
    all: {
      read: () => permOperations.all,
      write: () => getOperationsList('all', 'write'),
      modify: () => getOperationsList('all', 'modify')
    },
    global: {
      read: () => permOperations.global,
      write: () => getOperationsList('global', 'write'),
      modify: () => getOperationsList('global', 'modify')
    }
  };

  return (operationsStrategy[type]?.[op] ?? (() => []))();
};

const scopedResourceIds = new Set<ResourceType>();
const globalResourceIds = new Set<ResourceType>();

permissions.forEach((perm) => {
  if (perm.type === 'scoped') {
    scopedResourceIds.add(perm.resource);
  } else {
    globalResourceIds.add(perm.resource);
  }
});

export class PermissionsService {
  static canReadOwn = PermissionsService.permChecker<'own', 'read'>('own', 'read');
  static canWriteOwn = PermissionsService.permChecker<'own', 'write'>('own', 'write');
  static canModifyOwn = PermissionsService.permChecker<'own', 'modify'>('own', 'modify');
  static canReadGroup = PermissionsService.permChecker<'group', 'read'>('group', 'read');
  static canWriteGroup = PermissionsService.permChecker<'group', 'write'>('group', 'write');
  static canModifyGroup = PermissionsService.permChecker<'group', 'modify'>('group', 'modify');
  static canReadAll = PermissionsService.permChecker<'all', 'read'>('all', 'read');
  static canWriteAll = PermissionsService.permChecker<'all', 'write'>('all', 'write');
  static canModifyAll = PermissionsService.permChecker<'all', 'modify'>('all', 'modify');
  static canRead = PermissionsService.permChecker<'global', 'read'>('global', 'read');
  static canWrite = PermissionsService.permChecker<'global', 'write'>('global', 'write');
  static canModify = PermissionsService.permChecker<'global', 'modify'>('global', 'modify');
  isTenant: boolean = false;

  constructor() {
    queueMicrotask(() => {
      try {
        appUserStore.subscribe(
          (state) => state.isInitialized,
          async (isInitialized) => {
            if (isInitialized) {
              this.isTenant = this.userAccessLevel() === 'tenant';
            }
          }
        );
      } catch {
        // Circular imports can temporarily leave store bindings uninitialized.
      }
    });
  }

  userAccessLevel() {
    const { role } = appUserStore.getState();
    return role?.user_type;
  }

  //canAddOwn = this.permChecker<'own'>('own', 'write');

  static canInvoke(moduleId: Module) {
    const adminAccount = appTenantState.getState().tenantAccount;
    const licensedModules = adminAccount?.licensed_modules || [];
    const index = licensedModules.findIndex((module) => module === moduleId);

    return index !== -1;
  }

  verifyWriteAccess(memberId: string, resourceId: ResourceType) {
    return this.verifyAccessHelper(memberId, 'write', resourceId);
  }

  verifyModifyAccess(memberId: string, resourceId: ResourceType) {
    return this.verifyAccessHelper(memberId, 'modify', resourceId);
  }

  private verifyAccessHelper(
    memberId: string, // migrated from user_id to member_id
    operation: 'write' | 'modify' | 'read',
    resourceId: ResourceType
  ) {
    // Check if this is a global resource (not scoped to own/group/all)
    const isGlobalResource = globalResourceIds.has(resourceId);

    if (isGlobalResource) {
      // For global resources, check global permissions
      const checker = PermissionsService.permChecker('global', operation);
      try {
        return checker(resourceId as OperationResourceMap['global'][typeof operation]);
      } catch {
        return false;
      }
    }

    const canWrite = (type: 'group' | 'own' | 'all') => {
      // For backward compatibility, we need to handle any ResourceType
      // Cast to appropriate type based on what's valid for this operation
      const checker = PermissionsService.permChecker(type, operation);
      try {
        return checker(resourceId as OperationResourceMap[typeof type][typeof operation]);
      } catch {
        return false;
      }
    };

    const canWriteGroup = canWrite('group');
    const canWriteAll = canWrite('all');
    const canWriteOwn = canWrite('own');

    if (canWriteAll) {
      return true;
    }

    if (canWriteGroup) {
      return true;
    }

    const currentMember = appUserStore.getState().currentMember;
    if (currentMember?.id === memberId || currentMember?.user_id === memberId) {
      // backward compatibility
      return canWriteOwn;
    }

    return false;
  }

  static permChecker<
    Lvl extends keyof OperationResourceMap,
    Op extends keyof OperationResourceMap[Lvl]
  >(type: Lvl, operation: Op): (resource: OperationResourceMap[Lvl][Op]) => boolean {
    return (resourceId: OperationResourceMap[Lvl][Op]): boolean => {
      const operationsToPerform = permissionsCheck(type, operation as 'read' | 'write' | 'modify');

      const { role } = appUserStore.getState() || {};

      const resourcePermIds = permissions.reduce<Permission['id'][]>(
        (acc, perm) =>
          perm.resource === resourceId && operationsToPerform.includes(perm.operation)
            ? acc.concat(perm.id)
            : acc,
        []
      );

      return resourcePermIds.some((id) => role?.permissions?.includes(id));
    };
  }
}

export const permissionsService = new PermissionsService();
